import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  loadAllPapersForStudy,
  createPaper,
  archivePaper,
} from '../data/papersHelpers';
import { useActiveStudy } from '../hooks/useActiveStudy';
import {
  UploadCloud,
  Trash2,
  PlusCircle,
  AlertTriangle,
  Save,
  Users,
  Book,
  CheckCircle,
  Loader,
  X,
  Eye,
  ChevronLeft,
  Search,
  ChevronRight,
  Shield,
  User,
  Edit,
} from 'lucide-react';
import PDFViewer from '../components/PDFViewer';
import SearchableMultiSelect from '../components/SearchableMultiSelect';
import { isEqual } from 'lodash';
import EditUserModal from '../components/EditUserModal';

const PaginationControls = ({
  currentPage,
  setCurrentPage,
  totalItems,
  itemsPerPage,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  if (totalPages <= 1) return null;

  return (
    <div className="flex justify-end items-center gap-2 mt-2">
      <button
        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        disabled={currentPage === 1}
        className="px-2 py-1 bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronLeft size={16} />
      </button>
      <span className="text-sm font-medium text-slate-600">
        Page {currentPage} of {totalPages}
      </span>
      <button
        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages}
        className="px-2 py-1 bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
};

const SearchInput = ({ searchTerm, setSearchTerm, placeholder }) => (
  <div className="relative mb-2">
    <Search
      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
      size={18}
    />
    <input
      type="text"
      placeholder={placeholder}
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      className="w-full p-2 pl-10 border border-slate-300 rounded-lg focus-academic"
    />
  </div>
);

const ITEMS_PER_PAGE = 5;

const validateConfig = (data) => {
  if (typeof data !== 'object' || data === null) return false;
  // Post-ticket-03: papers are optional on the config doc — they live in
  // the papers/{uuid} collection now. We still accept configs that carry
  // a legacy papers array for backward-compatible JSON uploads; they get
  // ignored below.
  if (data.papers !== undefined && !Array.isArray(data.papers)) return false;
  if (typeof data.users !== 'object' || data.users === null) return false;
  return true;
};

const AdminConfigPage = () => {
  const { studyId, orgId, loading: studyLoading } = useActiveStudy();
  const [config, setConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [papers, setPapers] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [papersLoading, setPapersLoading] = useState(true);

  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState({ message: '', type: 'idle' });
  const [newPaper, setNewPaper] = useState({ title: '', pdf_url: '' });
  const [newUser, setNewUser] = useState({
    email: '',
    role: 'reviewer',
    assignedPapers: [],
  });
  const [previewingPaper, setPreviewingPaper] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const fileInputRef = useRef(null);

  const [paperSearch, setPaperSearch] = useState('');
  const [paperPage, setPaperPage] = useState(1);
  const [userSearch, setUserSearch] = useState('');
  const [adminPage, setAdminPage] = useState(1);
  const [researcherPage, setResearcherPage] = useState(1);

  useEffect(() => {
    // Ticket 11: configuration/main is denied by strict rules. This
    // page's user-management UI is legacy — ticket 17 replaces it with
    // the proper org-admin member management view. For now we start
    // with an empty-shaped config so the admin panel still renders
    // its paper CRUD column. The users section below will show the
    // empty state, which is fine because there are no unmigrated
    // users post-ticket-10.
    setConfig({ users: {} });
    setOriginalConfig({ users: {} });
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (config && originalConfig) {
      setHasUnsavedChanges(!isEqual(config, originalConfig));
    }
  }, [config, originalConfig]);

  // Ticket 03: papers now live in the papers/{uuid} collection. Load them
  // independently of the config doc so the admin view reflects the real
  // source of truth.
  const reloadPapers = async () => {
    setPapersLoading(true);
    try {
      const fresh = await loadAllPapersForStudy(studyId);
      setPapers(fresh);
    } catch (err) {
      console.error('Failed to load papers from collection:', err);
      setPapers([]);
    } finally {
      setPapersLoading(false);
    }
  };
  useEffect(() => {
    if (!studyId) { setPapersLoading(false); return; }
    reloadPapers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyId]);

  useEffect(() => {
    if (newUser.role === 'orgAdmin') {
      setNewUser((prev) => ({ ...prev, assignedPapers: [] }));
    }
  }, [newUser.role]);

  const filteredPapers = useMemo(() => {
    return papers.filter((paper) =>
      (paper.title || '').toLowerCase().includes(paperSearch.toLowerCase())
    );
  }, [papers, paperSearch]);

  const paginatedPapers = useMemo(() => {
    const startIndex = (paperPage - 1) * ITEMS_PER_PAGE;
    return filteredPapers.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredPapers, paperPage]);

  const { admins, researchers } = useMemo(() => {
    const allUsers = config ? Object.entries(config.users) : [];
    const filteredUsers = allUsers.filter(([email]) =>
      email.toLowerCase().includes(userSearch.toLowerCase())
    );

    return {
      admins: filteredUsers
        .filter(([, user]) => user.role === 'orgAdmin')
        .sort(([a], [b]) => a.localeCompare(b)),
      researchers: filteredUsers
        .filter(([, user]) => user.role === 'reviewer')
        .sort(([a], [b]) => a.localeCompare(b)),
    };
  }, [config, userSearch]);

  const paginatedAdmins = useMemo(() => {
    const startIndex = (adminPage - 1) * ITEMS_PER_PAGE;
    return admins.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [admins, adminPage]);

  const paginatedResearchers = useMemo(() => {
    const startIndex = (researcherPage - 1) * ITEMS_PER_PAGE;
    return researchers.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [researchers, researcherPage]);

  useEffect(() => setPaperPage(1), [paperSearch]);
  useEffect(() => {
    setAdminPage(1);
    setResearcherPage(1);
  }, [userSearch]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/json') {
      setStatus({
        message: 'Invalid file type. Please upload a .json file.',
        type: 'error',
      });
      return;
    }

    const text = await file.text();
    try {
      const uploadedConfig = JSON.parse(text);
      if (validateConfig(uploadedConfig)) {
        setConfig(uploadedConfig);
        setStatus({
          message: 'JSON file validated. Please review and save.',
          type: 'info',
        });
      } else {
        setStatus({
          message: 'Invalid JSON format. Please check file structure.',
          type: 'error',
        });
      }
    } catch (error) {
      setStatus({ message: 'Error parsing JSON file.', type: 'error' });
    }
    e.target.value = null;
  };

  const handleDiscardChanges = () => {
    if (
      window.confirm('Are you sure you want to discard all unsaved changes?')
    ) {
      setConfig(JSON.parse(JSON.stringify(originalConfig)));
      setStatus({ message: 'Changes discarded.', type: 'info' });
    }
  };

  const handleSaveConfig = async () => {
    setStatus({ message: 'Saving configuration...', type: 'loading' });

    // Ticket 03: papers no longer live in configuration/main. The paper
    // array is stripped from the saved payload — papers are authored via
    // createPaper/archivePaper against the papers/{uuid} collection, and
    // configuration/main is only used for users/assignedPapers during
    // the phase-1 transition. ID re-sequencing is deleted entirely — it
    // was the source of silent response-to-paper corruption.
    const newConfig = JSON.parse(JSON.stringify(config));
    delete newConfig.papers;
    newConfig._papersDeprecated = true;

    try {
      const configDocRef = doc(db, 'configuration', 'main');
      await setDoc(configDocRef, newConfig, { merge: true });
      setConfig({ ...newConfig, papers: config.papers || [] });
      setOriginalConfig(JSON.parse(JSON.stringify(config)));
      setStatus({
        message: 'User configuration saved.',
        type: 'success',
      });
    } catch (error) {
      console.error('Error saving config:', error);
      setStatus({ message: `Save failed: ${error.message}`, type: 'error' });
    }
  };

  const handleAddPaper = async (e) => {
    e.preventDefault();
    if (!newPaper.title || !newPaper.pdf_url) {
      setStatus({
        message: 'Paper title and PDF URL are required.',
        type: 'error',
      });
      return;
    }
    setStatus({ message: 'Adding paper...', type: 'loading' });
    try {
      await createPaper({
        title: newPaper.title,
        url: newPaper.pdf_url,
        studyId,
        orgId,
        addedBy: 'admin',
      });
      setNewPaper({ title: '', pdf_url: '' });
      await reloadPapers();
      setStatus({ message: 'Paper added.', type: 'success' });
    } catch (err) {
      console.error('createPaper failed:', err);
      setStatus({ message: `Failed to add paper: ${err.message}`, type: 'error' });
    }
  };

  const handleRemovePaper = async (paperId) => {
    if (
      !window.confirm(
        'Archive this paper? It will disappear from reviewer sidebars but its existing responses will be preserved.'
      )
    ) {
      return;
    }
    setStatus({ message: 'Archiving paper...', type: 'loading' });
    try {
      await archivePaper(paperId);
      // Unassign the (now-archived) paper from every user in the config
      // doc. Reviewers' sidebars already hide archived papers via
      // loadPapersForStudy, but keeping assignedPapers clean avoids
      // confusion in AdminDashboard and EditUserModal.
      setConfig((prev) => {
        const newUsers = { ...prev.users };
        Object.keys(newUsers).forEach((email) => {
          newUsers[email].assignedPapers = (
            newUsers[email].assignedPapers || []
          ).filter((id) => id !== paperId);
        });
        return { ...prev, users: newUsers };
      });
      await reloadPapers();
      setStatus({ message: 'Paper archived. Save to update user assignments.', type: 'info' });
    } catch (err) {
      console.error('archivePaper failed:', err);
      setStatus({ message: `Failed to archive paper: ${err.message}`, type: 'error' });
    }
  };

  const handleUpdateUserPapers = (email, newPaperIds) => {
    setConfig((prev) => {
      const newUsers = { ...prev.users };
      if (newUsers[email]) {
        newUsers[email].assignedPapers = newPaperIds;
      }
      return { ...prev, users: newUsers };
    });
    setEditingUser(null);
    setStatus({
      message: 'User paper assignments updated. Save to confirm changes.',
      type: 'info',
    });
  };

  const handleAddUser = (e) => {
    e.preventDefault();
    if (!newUser.email) {
      setStatus({ message: 'User email is required.', type: 'error' });
      return;
    }
    const email = newUser.email.toLowerCase().trim();
    if (config.users[email]) {
      setStatus({
        message: 'User with this email already exists.',
        type: 'error',
      });
      return;
    }
    setConfig((prev) => ({
      ...prev,
      users: {
        ...prev.users,
        [email]: {
          role: newUser.role,
          assignedPapers:
            newUser.role === 'orgAdmin' ? [] : newUser.assignedPapers.map(Number),
        },
      },
    }));
    setNewUser({ email: '', role: 'reviewer', assignedPapers: [] });
    setStatus({ message: 'User added. Save to confirm.', type: 'info' });
  };

  const handleRemoveUser = (email) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      const { [email]: _, ...remainingUsers } = config.users;
      setConfig((prev) => ({ ...prev, users: remainingUsers }));
      setStatus({ message: 'User removed. Save to confirm.', type: 'info' });
    }
  };

  const statusColors = {
    idle: 'bg-slate-100 text-slate-700',
    info: 'bg-blue-100 text-blue-800',
    success: 'bg-green-100 text-green-800',
    error: 'bg-red-100 text-red-800',
    loading: 'bg-yellow-100 text-yellow-800 animate-pulse',
  };

  if (isLoading || studyLoading || papersLoading || !config) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader className="animate-spin text-blue-600" size={48} />
      </div>
    );
  }

  return (
    <>
      <main className="p-4 lg:p-8 pb-32">
        <div className="mb-6">
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-800"
          >
            <ChevronLeft size={18} />
            Back to Progress Overview
          </Link>
        </div>
        <div className="bg-white p-6 rounded-xl border border-slate-200 academic-shadow-lg">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">
            Configuration Management
          </h2>
          <div
            className={`p-3 rounded-lg mb-6 text-sm flex items-center gap-3 transition-colors ${
              statusColors[status.type]
            }`}
          >
            {status.type === 'error' && <AlertTriangle className="h-5 w-5" />}
            {status.type === 'success' && <CheckCircle className="h-5 w-5" />}
            {status.type === 'loading' && (
              <Loader className="h-5 w-5 animate-spin" />
            )}
            <span className="flex-grow">{status.message}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-700">
                  <Book size={20} /> Manage Papers ({papers.length})
                </h3>
                <SearchInput
                  searchTerm={paperSearch}
                  setSearchTerm={setPaperSearch}
                  placeholder="Search papers by title..."
                />
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="p-3 text-left font-semibold text-slate-600">
                          Title
                        </th>
                        <th className="p-3 text-left font-semibold text-slate-600 w-40">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPapers.map((paper) => (
                        <tr
                          key={paper.id}
                          className="border-t border-slate-200"
                        >
                          <td className="p-3 align-top">
                            <span className="font-medium text-slate-800">
                              {paper.title}
                            </span>
                          </td>
                          <td className="p-3 align-top whitespace-nowrap">
                            <button
                              onClick={() => setPreviewingPaper(paper)}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors mr-2"
                              title="Preview PDF"
                            >
                              <Eye size={14} />
                            </button>
                            <button
                              onClick={() => handleRemovePaper(paper.id)}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                              title="Delete Paper"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredPapers.length === 0 && (
                    <p className="text-center p-4 text-slate-500">
                      No papers found for "{paperSearch}".
                    </p>
                  )}
                </div>
                <PaginationControls
                  currentPage={paperPage}
                  setCurrentPage={setPaperPage}
                  totalItems={filteredPapers.length}
                  itemsPerPage={ITEMS_PER_PAGE}
                />
                <form
                  onSubmit={handleAddPaper}
                  className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3"
                >
                  <h4 className="font-semibold text-slate-800">
                    Add New Paper
                  </h4>
                  <div>
                    <label
                      htmlFor="paperTitle"
                      className="block text-sm font-medium text-slate-700 mb-1"
                    >
                      Paper Title
                    </label>
                    <input
                      id="paperTitle"
                      type="text"
                      placeholder="Enter the full paper title"
                      value={newPaper.title}
                      onChange={(e) =>
                        setNewPaper({ ...newPaper, title: e.target.value })
                      }
                      className="w-full p-2 border border-slate-300 rounded-lg focus-academic"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="paperUrl"
                      className="block text-sm font-medium text-slate-700 mb-1"
                    >
                      PDF URL
                    </label>
                    <input
                      id="paperUrl"
                      type="url"
                      placeholder="https://example.com/paper.pdf"
                      value={newPaper.pdf_url}
                      onChange={(e) =>
                        setNewPaper({ ...newPaper, pdf_url: e.target.value })
                      }
                      className="w-full p-2 border border-slate-300 rounded-lg focus-academic"
                    />
                  </div>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    <PlusCircle size={16} /> Add Paper
                  </button>
                </form>
              </div>
            </div>

            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-700">
                  <Users size={20} /> Manage Users (
                  {Object.keys(config.users).length})
                </h3>
                <SearchInput
                  searchTerm={userSearch}
                  setSearchTerm={setUserSearch}
                  placeholder="Search users by email..."
                />
              </div>

              <div className="space-y-2">
                <h4 className="flex items-center gap-2 font-semibold text-slate-600">
                  <Shield size={16} /> Administrators ({admins.length})
                </h4>
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="p-3 text-left font-semibold text-slate-600">
                          Email
                        </th>
                        <th className="p-3 text-left font-semibold text-slate-600 w-24">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedAdmins.map(([email]) => (
                        <tr key={email} className="border-t border-slate-200">
                          <td className="p-3 align-top font-medium text-slate-800 break-all">
                            {email}
                          </td>
                          <td className="p-3 align-top">
                            <button
                              onClick={() => handleRemoveUser(email)}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                              title="Delete User"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {admins.length === 0 && (
                    <p className="text-center p-4 text-slate-500">
                      No administrators found.
                    </p>
                  )}
                </div>
                <PaginationControls
                  currentPage={adminPage}
                  setCurrentPage={setAdminPage}
                  totalItems={admins.length}
                  itemsPerPage={ITEMS_PER_PAGE}
                />
              </div>

              <div className="space-y-2">
                <h4 className="flex items-center gap-2 font-semibold text-slate-600">
                  <User size={16} /> Researchers ({researchers.length})
                </h4>
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="p-3 text-left font-semibold text-slate-600">
                          Email
                        </th>
                        <th className="p-3 text-left font-semibold text-slate-600">
                          Assigned
                        </th>
                        <th className="p-3 text-left font-semibold text-slate-600 w-40">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedResearchers.map(([email, user]) => (
                        <tr key={email} className="border-t border-slate-200">
                          <td className="p-3 align-top font-medium text-slate-800 break-all">
                            {email}
                          </td>
                          <td className="p-3 align-top">
                            {user.assignedPapers.length}
                          </td>
                          <td className="p-3 align-top whitespace-nowrap">
                            <button
                              onClick={() => setEditingUser({ email, ...user })}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors mr-2"
                              title="Edit Paper Assignments"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              onClick={() => handleRemoveUser(email)}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                              title="Delete User"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {researchers.length === 0 && (
                    <p className="text-center p-4 text-slate-500">
                      No researchers found.
                    </p>
                  )}
                </div>
                <PaginationControls
                  currentPage={researcherPage}
                  setCurrentPage={setResearcherPage}
                  totalItems={researchers.length}
                  itemsPerPage={ITEMS_PER_PAGE}
                />
              </div>

              <form
                onSubmit={handleAddUser}
                className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-4"
              >
                <h4 className="font-semibold text-slate-800">Add New User</h4>
                <div>
                  <label
                    htmlFor="userEmail"
                    className="block text-sm font-medium text-slate-700 mb-1"
                  >
                    User Email Address
                  </label>
                  <input
                    id="userEmail"
                    type="email"
                    placeholder="researcher@example.com"
                    value={newUser.email}
                    onChange={(e) =>
                      setNewUser({ ...newUser, email: e.target.value })
                    }
                    className="w-full p-2 border border-slate-300 rounded-lg focus-academic"
                  />
                </div>
                <div>
                  <label
                    htmlFor="userRole"
                    className="block text-sm font-medium text-slate-700 mb-1"
                  >
                    Role
                  </label>
                  <select
                    id="userRole"
                    value={newUser.role}
                    onChange={(e) =>
                      setNewUser({ ...newUser, role: e.target.value })
                    }
                    className="w-full p-2 border border-slate-300 rounded-lg bg-white focus-academic"
                  >
                    <option value="reviewer">Reviewer</option>
                    <option value="orgAdmin">Org admin</option>
                  </select>
                </div>
                <SearchableMultiSelect
                  options={papers}
                  selectedOptions={newUser.assignedPapers}
                  onChange={(selected) =>
                    setNewUser({ ...newUser, assignedPapers: selected })
                  }
                  isDisabled={newUser.role === 'orgAdmin'}
                />
                <button
                  type="submit"
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  <PlusCircle size={16} /> Add User
                </button>
              </form>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-700 flex items-center gap-2">
                  <UploadCloud size={20} /> Upload Configuration
                </h3>
                <button
                  onClick={() => fileInputRef.current.click()}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-200 transition-colors font-medium"
                >
                  Upload config.json
                </button>
                <input
                  type="file"
                  accept=".json"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
            </div>
          </div>
        </div>

        {previewingPaper && (
          <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-40 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full h-[90vh] flex flex-col shadow-2xl animate-fade-in-up">
              <div className="flex-shrink-0 p-2 border-b border-slate-200 flex justify-end">
                <button
                  onClick={() => setPreviewingPaper(null)}
                  className="p-2 rounded-full hover:bg-slate-200 transition-colors"
                >
                  <X size={24} className="text-slate-600" />
                </button>
              </div>
              <div className="flex-grow min-h-0">
                {/* PDFViewer predates ticket 17 and reads paper.pdf_url;
                    shim the new {url} shape until ticket 17 rewrites it. */}
                <PDFViewer
                  paper={{
                    ...previewingPaper,
                    pdf_url: previewingPaper.url || previewingPaper.pdf_url,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {editingUser && (
          <EditUserModal
            user={editingUser}
            allPapers={papers}
            onClose={() => setEditingUser(null)}
            onSave={handleUpdateUserPapers}
          />
        )}
      </main>

      {hasUnsavedChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-30 animate-slide-in-top">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
            <p className="text-sm font-semibold text-blue-700">
              You have unsaved changes.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={handleDiscardChanges}
                className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                Discard
              </button>
              <button
                onClick={handleSaveConfig}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
              >
                <Save size={18} /> Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminConfigPage;
